/**
 * Cross-subdomain authentication workaround for iOS
 * Handles cases where Keycloak cookies are not shared between subdomains
 */

import { isIOSDevice } from './iosDetection';

/**
 * Check if we're in a cross-subdomain authentication scenario
 */
export const isCrossSubdomainScenario = (): boolean => {
    const hostname = window.location.hostname;
    
    // Check if we're on checkout subdomain
    const isCheckoutDomain = hostname.includes('checkout.vseth.ethz.ch');
    
    // Check if we have any Keycloak cookies (indicating we should be authenticated)
    const hasKeycloakCookies = document.cookie
        .split(';')
        .some(cookie => cookie.trim().includes('kc-') || 
                       cookie.trim().includes('keycloak') ||
                       cookie.trim().includes('token') ||
                       cookie.trim().includes('idToken'));
    
    return isCheckoutDomain && !hasKeycloakCookies;
};

/**
 * Sanitize cookie value for logging (hide sensitive data)
 */
const sanitizeCookieValue = (cookie: string): string => {
    const [name, value] = cookie.split('=');
    if (!value) return cookie;
    
    // Hide sensitive cookie values
    const sensitiveNames = ['token', 'idtoken', 'kc-access', 'kc-refresh', 'session', 'auth'];
    if (sensitiveNames.some(sensitive => name.toLowerCase().includes(sensitive))) {
        return `${name}=[REDACTED]`;
    }
    
    // Truncate long values
    if (value.length > 20) {
        return `${name}=${value.substring(0, 20)}...[TRUNCATED]`;
    }
    
    return cookie;
};

/**
 * Extract authentication information from available cookies
 */
export const getAvailableAuthInfo = () => {
    const cookies = document.cookie.split(';').map(c => c.trim());
    const sanitizedCookies = cookies.map(sanitizeCookieValue);
    
    const authInfo = {
        hasKeycloakCookies: false,
        hasMarketingCookie: false,
        availableCookies: sanitizedCookies, // Use sanitized version
        keycloakCookies: cookies.filter(c => 
            c.includes('kc-') || 
            c.includes('keycloak') || 
            c.includes('token') || 
            c.includes('idToken')
        ).map(sanitizeCookieValue), // Sanitize keycloak cookies too
        marketingCookies: cookies.filter(c => 
            c.includes('logged_out_marketing_header_id') ||
            c.includes('marketing')
        ).map(sanitizeCookieValue) // Sanitize marketing cookies too
    };
    
    authInfo.hasKeycloakCookies = cookies.some(c => 
        c.includes('kc-') || 
        c.includes('keycloak') || 
        c.includes('token') || 
        c.includes('idToken')
    );
    authInfo.hasMarketingCookie = cookies.some(c => 
        c.includes('logged_out_marketing_header_id') ||
        c.includes('marketing')
    );
    
    return authInfo;
};

/**
 * Check if authentication should work based on available cookies
 */
export const shouldAuthenticationWork = (): boolean => {
    const authInfo = getAvailableAuthInfo();
    
    // If we have Keycloak cookies, authentication should work
    if (authInfo.hasKeycloakCookies) {
        return true;
    }
    
    // If we're on iOS and have marketing cookies, we might have partial auth
    if (isIOSDevice() && authInfo.hasMarketingCookie) {
        console.log('iOS detected with marketing cookies - partial authentication may be available');
        return false; // Still need proper Keycloak cookies
    }
    
    return false;
};

/**
 * Get detailed authentication status for debugging
 */
export const getCrossSubdomainAuthStatus = () => {
    const isIOS = isIOSDevice();
    const isCrossSubdomain = isCrossSubdomainScenario();
    const authInfo = getAvailableAuthInfo();
    const shouldWork = shouldAuthenticationWork();
    
    return {
        isIOS,
        isCrossSubdomain,
        currentDomain: window.location.hostname,
        authInfo,
        shouldAuthenticationWork: shouldWork,
        recommendation: shouldWork ? 
            'Authentication should work normally' : 
            'Keycloak cookies not available - may need to re-authenticate'
    };
};

/**
 * Check if we're in a test or staging environment (domain contains "test" or "staging")
 */
export const isTestEnvironment = (): boolean => {
    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes('test') || hostname.includes('staging');
};

/**
 * Log cross-subdomain authentication status for debugging
 * Only logs in test or staging environments (domain contains "test" or "staging")
 */
export const logCrossSubdomainAuthStatus = (): void => {
    if (!isTestEnvironment()) {
        return; // Skip logging in production
    }
    
    const status = getCrossSubdomainAuthStatus();
    
    console.log('=== CROSS-SUBDOMAIN AUTH STATUS ===');
    console.log('Current domain:', status.currentDomain);
    console.log('Is iOS:', status.isIOS);
    console.log('Is cross-subdomain scenario:', status.isCrossSubdomain);
    console.log('Should authentication work:', status.shouldAuthenticationWork);
    console.log('Available cookies:', status.authInfo.availableCookies);
    console.log('Keycloak cookies:', status.authInfo.keycloakCookies);
    console.log('Marketing cookies:', status.authInfo.marketingCookies);
    console.log('Recommendation:', status.recommendation);
    
    if (status.isCrossSubdomain && !status.shouldAuthenticationWork) {
        console.warn('Cross-subdomain authentication issue detected');
        console.warn('Keycloak cookies are not shared between subdomains');
        console.warn('This may cause infinite loading on iOS devices');
    }
};
